// ---------------------------------------------------------------------------
// Global "Codex is working" tracker.
//
// Every Codex call funnels through codexExec() (src/ai/tauri.ts), so that single
// chokepoint brackets each run with begin/end here. The UI subscribes via
// useSyncExternalStore and shows one shared thinking animation whenever ANY
// generation is in flight — no screen has to wire its own loading flag.
//
// Each entry also carries:
//  - startedAt, so the UI can show elapsed time (distinguishing "slow" from "hung"),
//  - an optional cancel callback (kills the CLI child process), and
//  - a streaming "tail" (the last stdout line), for perceived-latency feedback.
//
// This module is intentionally framework-agnostic (no React import) so the AI
// layer can depend on it without pulling in the view layer.
// ---------------------------------------------------------------------------

type Listener = () => void;

interface ActivityEntry {
  label: string;
  startedAt: number;
  tail: string | null;
  cancel?: () => void;
}

let nextId = 1;
const active = new Map<number, ActivityEntry>();
const listeners = new Set<Listener>();

export interface CodexActivitySnapshot {
  /** True while one or more Codex calls are running. */
  active: boolean;
  /** Number of concurrent in-flight Codex calls. */
  count: number;
  /** Label of the most recently started call, e.g. "Grading your answer…". */
  label: string | null;
  /** When the most recent call started (ms epoch), for elapsed-time display. */
  startedAt: number | null;
  /** Last stdout line of the most recent call (streamed), if any. */
  tail: string | null;
  /** True when the most recent call can be cancelled. */
  cancellable: boolean;
}

// Cached snapshot so getSnapshot() returns a stable reference between changes —
// useSyncExternalStore relies on referential equality to avoid render loops.
let snapshot: CodexActivitySnapshot = {
  active: false,
  count: 0,
  label: null,
  startedAt: null,
  tail: null,
  cancellable: false,
};

function latest(): ActivityEntry | null {
  let entry: ActivityEntry | null = null;
  for (const e of active.values()) entry = e; // Map preserves insertion order.
  return entry;
}

function recompute() {
  const last = latest();
  snapshot = {
    active: active.size > 0,
    count: active.size,
    label: last?.label ?? null,
    startedAt: last?.startedAt ?? null,
    tail: last?.tail ?? null,
    cancellable: !!last?.cancel,
  };
}

function emit() {
  for (const listener of listeners) listener();
}

/** Mark a Codex call as started. Returns an id to pass to endCodexActivity. */
export function beginCodexActivity(label = 'Codex is thinking…', cancel?: () => void): number {
  const id = nextId++;
  active.set(id, { label, startedAt: Date.now(), tail: null, cancel });
  recompute();
  emit();
  return id;
}

/** Update the streamed "last output line" for a running call. */
export function setCodexActivityTail(id: number, tail: string): void {
  const entry = active.get(id);
  if (!entry) return;
  entry.tail = tail;
  recompute();
  emit();
}

/** Mark a Codex call as finished. Safe to call with an unknown id. */
export function endCodexActivity(id: number): void {
  if (active.delete(id)) {
    recompute();
    emit();
  }
}

/** Cancel every in-flight call that registered a cancel callback. */
export function cancelCodexActivity(): void {
  for (const entry of active.values()) {
    try {
      entry.cancel?.();
    } catch {
      /* cancellation is best-effort */
    }
  }
}

export function subscribeCodexActivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getCodexActivitySnapshot(): CodexActivitySnapshot {
  return snapshot;
}
